import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLayout } from '../../../hooks/screen/useLayout';
import { MainHead } from '../../../components/common/head/MainHead';
import { marketPricePageInfo, pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { AnimateLoading } from '../../../components/common/loading/AnimateLoading';
import { Helmet } from 'react-helmet';
import { EmptyResult } from './components/EmptyResult';
import { TractorValueDetailCard } from '../../../views/Product/listView/TractorValueDetailCard/TractorValueDetailCard';
import { IMarketPrice } from '../../../interfaces/MarketPrice.interfaces';
import styled from 'styled-components';
import { useGetMachineSearchData } from '../../../hooks/api/machines/queries/useGetMachineSearchData.query';

export const MarketPriceSearchResultPage = () => {
  const [searchParams] = useSearchParams();
  const [searchKeyword, setSearchKeyword] = useState<string>(' ');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const { data, isLoading: isGetMachinePriceStatisticsQueryLoading } = useGetMachineSearchData(
    {
      q: searchKeyword.trim(),
      filter: 'hasPriceStatistics=true',
    },
    {
      enabled: Boolean(searchKeyword.trim()),
    },
  );

  const searchedMachines = data?.hits;

  useLayout({
    fixedHeader: (
      <MainHead
        backButton={{ isShow: true }}
        searchInput={{
          searchKeyword,
          setSearchKeyword,
          inputRef,
          isResultPage: true,
          searchInputType: 'marketPrice',
        }}
      />
    ),
    fixedHeaderHeight: 48,
    fixedFooter: <></>,
    fixedFooterHeight: 0,
  });

  const transformedMachineData = useMemo<IMarketPrice[]>(() => {
    if (searchedMachines && searchedMachines.length > 0) {
      return searchedMachines.map((machine) => {
        return {
          brand: machine.brand,
          machine: machine.machine,
          minPrice: machine.priceStatistics?.minPrice ?? 0,
          maxPrice: machine.priceStatistics?.maxPrice ?? 0,
          views: 0,
          priceCount: machine.priceStatistics?.priceCount ?? 0,
        };
      });
    }

    return [];
  }, [searchedMachines]);

  useEffect(() => {
    const keyword = searchParams.get('keyword');

    if (keyword) {
      setSearchKeyword(keyword);
    } else {
      navigate(marketPricePageInfo.listPage.path, { replace: true });
    }
  }, [searchParams, navigate]);

  if (isGetMachinePriceStatisticsQueryLoading) {
    return <AnimateLoading />;
  }

  if (transformedMachineData && transformedMachineData.length === 0) {
    return <EmptyResult />;
  }

  if (transformedMachineData && transformedMachineData.length !== 0) {
    return (
      <>
        <Helmet>
          <title>{marketPricePageInfo.listPage.title}</title>
        </Helmet>
        <MarketPriceListWrapper>
          <MarketPriceCardGroup>
            {transformedMachineData.map((machine) => (
              <TractorValueDetailCard key={machine.machine.id} product={machine} />
            ))}
          </MarketPriceCardGroup>
        </MarketPriceListWrapper>
      </>
    );
  }

  return null;
};

const MarketPriceListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const MarketPriceCardGroup = styled.div`
  display: flex;
  padding: 16px 16px 32px 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  align-self: stretch;
`;
